import { supabase } from '../lib/supabase';
import { getAssetSignedUrl } from './projectAssets';

const BUCKET = 'video-project-assets';
const THUMB_W = 480;
const THUMB_H = 270;
const JPEG_QUALITY = 0.82;
const MAX_SEEK_WAIT_MS = 4000;

type CoverClipType = 'video' | 'ai-generated' | 'image';

export interface CoverClipLike {
  id: string;
  type: string;
  startTime: number;
  duration: number;
  trackIndex?: number;
  mediaOffset?: number;
  storagePath?: string;
  src?: string;
  aiStatus?: string;
}

const isCoverType = (t: string): t is CoverClipType =>
  t === 'video' || t === 'ai-generated' || t === 'image';

export function findCoverClipAtZero<T extends CoverClipLike>(clips: T[]): T | null {
  const eligible = clips.filter(c => {
    if (!isCoverType(c.type)) return false;
    if (c.type === 'ai-generated' && c.aiStatus && c.aiStatus !== 'ready') return false;
    if (c.startTime > 0.05) return false;
    if (c.startTime + c.duration <= 0.05) return false;
    return c.storagePath || c.src;
  });
  if (eligible.length === 0) return null;
  return eligible.reduce((best, c) =>
    (c.trackIndex ?? 0) > (best.trackIndex ?? 0) ? c : best,
  );
}

export function hashCoverClip(clip: CoverClipLike | null): string {
  if (!clip) return 'none';
  return [
    clip.id,
    clip.type,
    clip.storagePath || clip.src || '',
    Math.round((clip.mediaOffset ?? 0) * 100),
  ].join('|');
}

async function resolveClipUrl(clip: CoverClipLike): Promise<string | null> {
  if (clip.storagePath) {
    const url = await getAssetSignedUrl(clip.storagePath).catch(() => null);
    if (url) return url;
  }
  if (clip.src && /^https?:|^blob:|^data:/.test(clip.src)) return clip.src;
  return null;
}

function drawCovered(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  srcW: number,
  srcH: number,
): void {
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, THUMB_W, THUMB_H);
  if (!srcW || !srcH) return;
  const scale = Math.max(THUMB_W / srcW, THUMB_H / srcH);
  const drawW = srcW * scale;
  const drawH = srcH * scale;
  const dx = (THUMB_W - drawW) / 2;
  const dy = (THUMB_H - drawH) / 2;
  ctx.drawImage(source, dx, dy, drawW, drawH);
}

async function captureVideoFrame(url: string, mediaOffset: number): Promise<Blob | null> {
  return new Promise(resolve => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.src = url;

    let done = false;
    const finish = (blob: Blob | null) => {
      if (done) return;
      done = true;
      video.src = '';
      video.load();
      resolve(blob);
    };

    const timer = window.setTimeout(() => finish(null), MAX_SEEK_WAIT_MS);

    const draw = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = THUMB_W;
        canvas.height = THUMB_H;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          window.clearTimeout(timer);
          return finish(null);
        }
        drawCovered(ctx, video, video.videoWidth, video.videoHeight);
        canvas.toBlob(b => {
          window.clearTimeout(timer);
          finish(b);
        }, 'image/jpeg', JPEG_QUALITY);
      } catch {
        window.clearTimeout(timer);
        finish(null);
      }
    };

    video.onloadedmetadata = () => {
      const target = Math.max(0, Math.min(mediaOffset || 0, (video.duration || 0) - 0.05));
      try {
        video.currentTime = target;
      } catch {
        draw();
      }
    };
    video.onseeked = draw;
    video.onerror = () => {
      window.clearTimeout(timer);
      finish(null);
    };
  });
}

async function captureImageFrame(url: string): Promise<Blob | null> {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    let done = false;
    const finish = (b: Blob | null) => {
      if (done) return;
      done = true;
      resolve(b);
    };
    const timer = window.setTimeout(() => finish(null), MAX_SEEK_WAIT_MS);
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = THUMB_W;
        canvas.height = THUMB_H;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          window.clearTimeout(timer);
          return finish(null);
        }
        drawCovered(ctx, img, img.naturalWidth, img.naturalHeight);
        canvas.toBlob(b => {
          window.clearTimeout(timer);
          finish(b);
        }, 'image/jpeg', JPEG_QUALITY);
      } catch {
        window.clearTimeout(timer);
        finish(null);
      }
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      finish(null);
    };
    img.src = url;
  });
}

async function captureFrameForClip(clip: CoverClipLike): Promise<Blob | null> {
  const url = await resolveClipUrl(clip);
  if (!url) return null;
  if (clip.type === 'image') return captureImageFrame(url);
  return captureVideoFrame(url, clip.mediaOffset ?? 0);
}

export interface RegenerateThumbnailParams {
  projectId: string;
  companyId: string;
  clip: CoverClipLike | null;
}

export async function regenerateProjectThumbnail({
  projectId,
  companyId,
  clip,
}: RegenerateThumbnailParams): Promise<string | null> {
  if (!clip) {
    await supabase
      .from('video_projects')
      .update({ thumbnail_file_name: null })
      .eq('id', projectId);
    return null;
  }

  const blob = await captureFrameForClip(clip);
  if (!blob) return null;

  const path = `${companyId}/${projectId}/thumbnail.jpg`;
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, { contentType: 'image/jpeg', upsert: true });
  if (upErr) return null;

  const { error: dbErr } = await supabase
    .from('video_projects')
    .update({ thumbnail_file_name: path })
    .eq('id', projectId);
  if (dbErr) return null;

  return path;
}

export async function getProjectThumbnailSignedUrl(path: string): Promise<string | null> {
  return getAssetSignedUrl(path);
}
